import * as Sentry from '@sentry/react';

import { SENTRY_DSN, SENTRY_ENVIRONMENT, SENTRY_RELEASE } from './config';

/**
 * Crash reporting for the browser half of the app.
 *
 * Worth having separately from the backend's: a render that throws, a chunk
 * that fails to load after a deploy, a `undefined is not an object` on one
 * person's phone — none of these ever reach the server, so the only evidence
 * today is somebody mentioning that the screen went blank.
 *
 * Errors only, no tracing and no session replay. Replay would record the
 * customer email and part numbers on screen into a third party, which is not a
 * trade worth making for an internal tool whose users sit down the hall.
 *
 * Without a DSN every Sentry call below is a no-op, which is how the dev server
 * and the test suite run.
 */
export function initErrorReporting(): void {
  if (!SENTRY_DSN) {
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,
    tracesSampleRate: 0,
    sendDefaultPii: false,
    // Extensions and the odd crawler throw inside their own injected scripts
    // and would otherwise fill the issue list with errors from code we do not
    // ship. A frame from our own bundle is the test of ours-or-not.
    ignoreErrors: ['ResizeObserver loop', 'Non-Error promise rejection captured'],
  });
}

/** Reports an error the app caught and handled by showing a failure screen. */
export function reportError(error: unknown): void {
  Sentry.captureException(error);
}

/**
 * Ties reports to a person, so "it broke for Maria twice this morning" is a
 * query rather than a guess. Id only — no name or email leaves the app.
 */
export function identifyForErrorReporting(userId: string | undefined): void {
  Sentry.setUser(userId ? { id: userId } : null);
}
